import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { campaignApi } from "../services/campaignApi";

const BORDER_COLOR = "#E8ECF1";

const segmentOptions = [
  { value: "all", label: "All contacts" },
  { value: "readers", label: "Often read" },
  { value: "ignores", label: "Unread / ignored" },
  { value: "interacts", label: "Interacts" },
];

const formatSegment = (value) => {
  if (!value) return "mixed";
  return String(value).replace(/_/g, " ");
};

const formatDateTime = (value, timezone = "Africa/Harare") => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

const segmentColor = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "interacts") return "success";
  if (normalized === "reads_often") return "primary";
  if (normalized === "ignores_often") return "warning";
  return "default";
};

export default function WhatsAppContactInsightsCard({ reloadToken, timezone = "Africa/Harare" }) {
  const [segment, setSegment] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });

  useEffect(() => {
    let active = true;
    const timeout = setTimeout(async () => {
      setLoading(true);
      setError("");

      try {
        const response = await campaignApi.contactInsights({
          segment,
          q: query || undefined,
          page,
          per_page: 12,
        });

        if (!active) return;

        const payload = response?.data ?? {};
        setRows(Array.isArray(payload?.data) ? payload.data : []);
        setMeta({
          currentPage: Number(payload?.current_page ?? 1),
          lastPage: Number(payload?.last_page ?? 1),
          total: Number(payload?.total ?? 0),
        });
      } catch (err) {
        if (!active) return;

        setRows([]);
        setMeta({ currentPage: 1, lastPage: 1, total: 0 });
        setError(err?.response?.data?.message || err?.message || "Failed to load contact insights");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, query ? 250 : 0);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [page, query, reloadToken, segment]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        bgcolor: "white",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 1.8, md: 2.1 } }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={950}>
              Contact Engagement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              See who often reads, who keeps messages unread, and who actively replies to broadcast-driven conversations.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              select
              label="Segment"
              value={segment}
              onChange={(event) => {
                setSegment(event.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 190 }}
            >
              {segmentOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Search contact"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 } }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ py: 7, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">No contact insights matched this filter yet.</Alert>
        ) : (
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: BORDER_COLOR,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Campaigns</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Read performance</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Interactions</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Segment</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.contact_id} hover>
                      <TableCell>
                        <Typography fontWeight={900}>{row.contact_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.phone || row.email || "No contact details"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={900}>
                          {Number(row.campaign_count ?? 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number(row.total_outbox ?? 0).toLocaleString()} sends
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={900}>
                          Read {Number(row.read_count ?? 0).toLocaleString()} / {Number(row.delivered_count ?? 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number(row.read_rate ?? 0).toFixed(1)}% read rate • {Number(row.unread_count ?? 0).toLocaleString()} unread
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={900}>
                          {Number(row.reply_conversation_count ?? 0).toLocaleString()} conversations
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number(row.inbound_message_count ?? 0).toLocaleString()} inbound replies
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={formatSegment(row.segment)}
                          color={segmentColor(row.segment)}
                          sx={{ textTransform: "capitalize", fontWeight: 800 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={800}>
                          {formatDateTime(
                            row.last_interaction_at || row.last_read_at || row.last_delivered_at || row.last_sent_at,
                            timezone
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {meta.total.toLocaleString()} contacts matched
              </Typography>

              <Pagination
                color="primary"
                page={meta.currentPage}
                count={Math.max(1, meta.lastPage)}
                onChange={(_, value) => setPage(value)}
              />
            </Stack>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
